import { z } from "zod";
export const RoleFeeKindSchema = z.enum([
  "performer",
  "passed_audition_performer",
  "choreographer",
  "stage_choreographer",
  "show_designer",
  "music_arranger",
]);
export type RoleFeeKind = z.infer<typeof RoleFeeKindSchema>;

export const ROLE_FEE_KIND_LABELS: Record<RoleFeeKind, string> = {
  performer: "ນັກສະແດງ",
  passed_audition_performer: "ນັກສະແດງທີ່ຜ່ານເກນ",
  choreographer: "ອອກແບບທ່າເຕັ້ນສຳລັບລິລະປິນ",
  stage_choreographer: "ອອກແບບທ່າເຕັ້ນ",
  show_designer: "ອອກແບບການສະແດງ",
  music_arranger: "ຄົນຕັດແຕ່ງເພງ",
};

/** Selectable show-base rates for music arranger assignments. */
export const MUSIC_ARRANGER_SHOW_BASE_AMOUNTS = [350_000, 650_000] as const;
export type MusicArrangerShowBaseAmount =
  (typeof MUSIC_ARRANGER_SHOW_BASE_AMOUNTS)[number];

export function isMusicArrangerShowBaseAmount(
  value: number,
): value is MusicArrangerShowBaseAmount {
  return (MUSIC_ARRANGER_SHOW_BASE_AMOUNTS as readonly number[]).includes(
    value,
  );
}

export const AssignmentPricingBreakdownSchema = z.object({
  roleFeeKind: RoleFeeKindSchema,
  showCount: z.number(),
  rehearsalDays: z.number(),
  showBaseAmount: z.number(),
  additionalShowPercent: z.number(),
  rehearsalDayAmount: z.number(),
  multiShowRehearsalPercent: z.number(),
  firstShowAmount: z.number(),
  additionalShowAmount: z.number(),
  rehearsalDayRate: z.number(),
  showAmount: z.number(),
  rehearsalAmount: z.number(),
  grossAmount: z.number(),
  lines: z.array(
    z.object({
      label: z.string(),
      quantity: z.number(),
      unitAmount: z.number(),
      amount: z.number(),
      eventShowId: z.string().optional(),
      showDate: z.coerce.date().optional(),
    }),
  ),
});
export type AssignmentPricingBreakdown = z.infer<
  typeof AssignmentPricingBreakdownSchema
>;

export const AssignmentSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  performerId: z.string(),
  performerName: z.string().optional(),
  roleFeeKind: RoleFeeKindSchema,
  showCount: z.number(),
  rehearsalDays: z.number(),
  showBaseAmount: z.number(),
  additionalShowPercent: z.number(),
  rehearsalDayAmount: z.number(),
  multiShowRehearsalPercent: z.number(),
  showAmount: z.number(),
  rehearsalAmount: z.number(),
  grossAmount: z.number(),
  pricingBreakdown: AssignmentPricingBreakdownSchema,
  lineKey: z.string(),
  notes: z.string().nullable(),
  eventShowIds: z.array(z.string()).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AssignmentDTO = z.infer<typeof AssignmentSchema>;

export const CreateAssignmentSchema = z
  .object({
    performerId: z.string().min(1, "ຕ້ອງເລືອກນັກສະແດງ"),
    roleFeeKind: RoleFeeKindSchema,
    /** Derived from event shows when omitted (Phase 23). Ignored when eventShowIds is set. */
    showCount: z.number().int().min(1, "ຈຳນວນໂຊຕ້ອງ ≥ 1").optional(),
    /** Selected event show ids — when set, showCount is derived server-side (Phase 26). */
    eventShowIds: z.array(z.string().min(1)).min(1).optional(),
    rehearsalDays: z.number().int().min(0, "ຈຳນວນວັນຊ້ອມຕ້ອງ ≥ 0"),
    /** Required when roleFeeKind is music_arranger (350,000 or 650,000). */
    showBaseAmountOverride: z.number().int().positive().optional(),
    lineKey: z.string().min(1).optional(),
    notes: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.roleFeeKind === "music_arranger") {
      if (
        data.showBaseAmountOverride == null ||
        !isMusicArrangerShowBaseAmount(data.showBaseAmountOverride)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຕ້ອງເລືອກອັດຕາຄ່າຕັດແຕ່ງເພງ (350,000 ຫຼື 650,000)",
          path: ["showBaseAmountOverride"],
        });
      }
      return;
    }
    if (data.showBaseAmountOverride != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ອັດຕາພິເສດໃຊ້ໄດ້ກັບຄົນຕັດແຕ່ງເພງເທົ່ານັ້ນ",
        path: ["showBaseAmountOverride"],
      });
    }
  });
export type CreateAssignmentDTO = z.infer<typeof CreateAssignmentSchema>;

export const BulkCreateAssignmentsSchema = z.object({
  assignments: z.array(CreateAssignmentSchema).min(1).max(50),
});
export type BulkCreateAssignmentsDTO = z.infer<
  typeof BulkCreateAssignmentsSchema
>;

export const BulkCreateAssignmentsResultSchema = z.object({
  created: z.array(AssignmentSchema),
  skipped: z.array(
    z.object({
      input: CreateAssignmentSchema,
      code: z.string(),
      message: z.string(),
    }),
  ),
});
export type BulkCreateAssignmentsResultDTO = z.infer<
  typeof BulkCreateAssignmentsResultSchema
>;

export const EventRehearsalSummarySchema = z.object({
  totalRehearsalDays: z.number(),
  totalRehearsalAmount: z.number(),
  byPerformer: z.array(
    z.object({
      performerId: z.string(),
      performerName: z.string(),
      rehearsalDays: z.number(),
      shootDays: z.number(),
      assignments: z.array(
        z.object({
          id: z.string(),
          roleFeeKind: RoleFeeKindSchema,
          lineKey: z.string(),
          quantity: z.number(),
          grossPreview: z.number(),
        }),
      ),
    }),
  ),
});
export type EventRehearsalSummaryDTO = z.infer<
  typeof EventRehearsalSummarySchema
>;

export const UpdateAssignmentSchema = z
  .object({
    roleFeeKind: RoleFeeKindSchema.optional(),
    showCount: z.number().int().min(1, "ຈຳນວນໂຊຕ້ອງ ≥ 1").optional(),
    eventShowIds: z.array(z.string().min(1)).min(1).optional(),
    rehearsalDays: z.number().int().min(0, "ຈຳນວນວັນຊ້ອມຕ້ອງ ≥ 0").optional(),
    showBaseAmountOverride: z.number().int().positive().optional(),
    lineKey: z.string().min(1).optional(),
    notes: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.showBaseAmountOverride != null &&
      !isMusicArrangerShowBaseAmount(data.showBaseAmountOverride)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ອັດຕາຄ່າຕັດແຕ່ງເພງຕ້ອງເປັນ 350,000 ຫຼື 650,000",
        path: ["showBaseAmountOverride"],
      });
    }
  });
export type UpdateAssignmentDTO = z.infer<typeof UpdateAssignmentSchema>;

export const BulkUpdateAssignmentShowsSchema = z.object({
  updates: z
    .array(
      z.object({
        assignmentId: z.string().min(1),
        eventShowIds: z.array(z.string().min(1)).min(1),
      }),
    )
    .min(1),
});
export type BulkUpdateAssignmentShowsDTO = z.infer<
  typeof BulkUpdateAssignmentShowsSchema
>;

export const BulkUpdateAssignmentShowsResultSchema = z.object({
  updated: z.array(AssignmentSchema),
  skipped: z.array(
    z.object({
      assignmentId: z.string(),
      code: z.string(),
      message: z.string(),
    }),
  ),
});
export type BulkUpdateAssignmentShowsResultDTO = z.infer<
  typeof BulkUpdateAssignmentShowsResultSchema
>;

export const AddEventShowToAllAssignmentsSchema = z.object({
  eventShowId: z.string().min(1),
});
export type AddEventShowToAllAssignmentsDTO = z.infer<
  typeof AddEventShowToAllAssignmentsSchema
>;

export const EventShowDeleteImpactSchema = z.object({
  linkedAssignmentCount: z.number(),
  blockedAssignmentCount: z.number(),
  blockedPerformers: z.array(
    z.object({
      performerId: z.string(),
      performerName: z.string().nullable(),
    }),
  ),
});
export type EventShowDeleteImpactDTO = z.infer<
  typeof EventShowDeleteImpactSchema
>;

export const PartialShowParticipationSchema = z.object({
  performerId: z.string(),
  performerName: z.string(),
  selectedCount: z.number(),
  totalShowCount: z.number(),
  eventShowIds: z.array(z.string()),
});
export type PartialShowParticipationDTO = z.infer<
  typeof PartialShowParticipationSchema
>;
